import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SectionNav.css';

/** Processed logo: white → transparent so only black ink is masked (PUBLIC_URL — not in CSS url()). */
const ROBOT_NAV_MASK = `${process.env.PUBLIC_URL || ''}/urbanix-robot-nav.png`;

const robotNavGlyphStyle = {
  WebkitMaskImage: `url(${ROBOT_NAV_MASK})`,
  maskImage: `url(${ROBOT_NAV_MASK})`,
};

const SECTIONS = [
  { id: 'section-summary', label: 'ריכוז נתונים', icon: 'fi-rr-file-invoice' },
  { id: 'section-plans', label: 'תוכניות', icon: 'fi-rr-document' },
  { id: 'section-market', label: 'נתוני שוק', icon: 'fi-rr-chart-line-up' },
  { id: 'section-eligibility', label: 'בדיקת זכאות', icon: 'fi-rr-checkbox' },
  { id: 'section-ai', label: 'ניתוח AI', robotNavIcon: true },
];

const SectionNav = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [visibleSections, setVisibleSections] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const lockedRef = useRef(null);

  const prevIdsRef = useRef('');

  useEffect(() => {
    const updateVisibleSections = () => {
      // Only count sections that exist AND are actually visible — under a focus
      // the non-focused sections are display:none (data-focus-hidden), so the
      // nav must drop them and adapt to the focused view.
      const found = SECTIONS.filter((s) => {
        const el = document.getElementById(s.id);
        return el && el.offsetParent !== null && el.getAttribute('data-focus-hidden') !== 'true';
      });
      // Order by actual on-page position, not the fixed array — under a focus the
      // chosen section leads above the summary (CSS order), so the nav must match.
      found.sort((a, b) => (
        document.getElementById(a.id).getBoundingClientRect().top -
        document.getElementById(b.id).getBoundingClientRect().top
      ));
      const ids = found.map(s => s.id).join(',');
      if (ids !== prevIdsRef.current) {
        prevIdsRef.current = ids;
        setVisibleSections(found);
        setIsVisible(found.length >= 2);
      }
    };

    updateVisibleSections();
    const interval = setInterval(updateVisibleSections, 1000);
    return () => clearInterval(interval);
  }, []);

  const updateActiveFromScroll = useCallback(() => {
    if (visibleSections.length === 0) return;
    if (lockedRef.current) return;

    // If at the bottom of the page, always select the last section
    const docHeight = document.documentElement.scrollHeight;
    const atBottom = (window.scrollY + window.innerHeight) >= (docHeight - 50);
    if (atBottom) {
      setActiveSection(visibleSections[visibleSections.length - 1].id);
      return;
    }

    const headerOffset = 140;
    const vpBottom = window.innerHeight;
    const vpCenter = (headerOffset + vpBottom) / 2;

    let bestId = null;
    let bestDist = Infinity;

    for (const { id } of visibleSections) {
      const el = document.getElementById(id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();

      if (rect.bottom < headerOffset || rect.top > vpBottom) continue;

      const visTop = Math.max(rect.top, headerOffset);
      const visBot = Math.min(rect.bottom, vpBottom);
      const center = (visTop + visBot) / 2;

      const dist = Math.abs(center - vpCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = id;
      }
    }

    if (bestId) {
      setActiveSection(bestId);
    }
  }, [visibleSections]);

  useEffect(() => {
    if (visibleSections.length === 0) return;

    window.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    updateActiveFromScroll();

    return () => window.removeEventListener('scroll', updateActiveFromScroll);
  }, [visibleSections, updateActiveFromScroll]);

  const scrollTo = (id) => {
    lockedRef.current = id;
    setActiveSection(id);

    const el = document.getElementById(id);
    if (!el) return;

    const header = document.querySelector('.main-header');
    const isExpanded = header && !header.classList.contains('scrolled');

    const doScroll = () => {
      const h = header ? header.offsetHeight : 0;
      const targetY = el.getBoundingClientRect().top + window.scrollY - h - 16;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
      setTimeout(() => {
        lockedRef.current = null;
        updateActiveFromScroll();
      }, 800);
    };

    if (isExpanded) {
      header.classList.add('no-transition', 'scrolled');
      void header.offsetHeight; // force reflow — triggers ResizeObserver → spacer update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          doScroll();
          header.classList.remove('no-transition');
        });
      });
    } else {
      doScroll();
    }
  };

  if (!isVisible) return null;

  return (
    <nav className="section-nav" aria-label="ניווט בין חלקי הדף">
      <div className="section-nav-track">
        {visibleSections.map(({ id, label, icon, robotNavIcon }) => (
          <button
            key={id}
            className={`section-nav-item ${activeSection === id ? 'active' : ''}`}
            onClick={() => scrollTo(id)}
            title={label}
          >
            {robotNavIcon ? (
              <span className="section-nav-icon" aria-hidden>
                <span className="section-nav-robot-glyph" style={robotNavGlyphStyle} />
              </span>
            ) : (
              <i className={`fi ${icon} section-nav-icon`} aria-hidden />
            )}
            <span className="section-nav-label">{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default SectionNav;
